import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PublicNav from '../components/PublicNav';

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

const BLUE = '#2563eb';
const WHITE = '#ffffff';
const TEXT = '#0a0a0a';
const TEXT_MUTED = '#555555';
const BORDER = '#e2e8f0';
const OFF_WHITE = '#f8fafc';
const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 36 }}>
      <h2 style={{ fontSize: 19, fontWeight: 800, color: TEXT, marginBottom: 12 }}>{title}</h2>
      <div style={{ fontSize: 15, color: TEXT_MUTED, lineHeight: 1.75 }}>{children}</div>
    </div>
  );
}

export default function Privacy() {
  const isMobile = useIsMobile();
  const px = isMobile ? 20 : 60;

  return (
    <div style={{ background: WHITE, minHeight: '100vh', fontFamily: FONT }}>
      <PublicNav isMobile={isMobile} />

      <div style={{ padding: isMobile ? `40px ${px}px 60px` : `64px ${px}px 80px`, maxWidth: 720, margin: '0 auto' }}>
        <div style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.1em', color: BLUE, marginBottom: 14 }}>Legal</div>
        <h1 style={{ fontSize: isMobile ? 30 : 40, fontWeight: 800, color: TEXT, letterSpacing: '-1px', lineHeight: 1.1, marginBottom: 10 }}>
          Privacy Policy
        </h1>
        <div style={{ fontSize: 13, color: TEXT_MUTED, marginBottom: 40 }}>Last updated: July 9, 2026</div>

        <Section title="What this covers">
          <p style={{ margin: 0 }}>
            This policy explains what information PreVisr ("we", "us") collects when you use previsr.com, how we use it, who we share it with, and the choices you have. By creating an account or using the site, you agree to the practices described here.
          </p>
        </Section>

        <Section title="Information we collect">
          <p style={{ marginBottom: 12 }}><strong style={{ color: TEXT }}>Account information.</strong> Your name, email address, and password (stored securely by our authentication provider, Supabase &mdash; we never see or store your raw password).</p>
          <p style={{ marginBottom: 12 }}><strong style={{ color: TEXT }}>Academic information you provide.</strong> MCAT score, GPA, science GPA, state of residence, application cycle, and which schools you save or track. You choose what to enter; none of it is required to create an account.</p>
          <p style={{ marginBottom: 12 }}><strong style={{ color: TEXT }}>Usage data.</strong> Checklist progress, onboarding responses, and basic device/browser information collected automatically when you use the site.</p>
          <p style={{ margin: 0 }}><strong style={{ color: TEXT }}>Payment information.</strong> If you subscribe to a paid plan, payment is processed directly by Stripe. We never receive or store your card number &mdash; we only receive confirmation that a payment succeeded and your subscription status.</p>
        </Section>

        <Section title="How we use your information">
          <p style={{ margin: 0 }}>
            We use your information to run the core product: matching your stats against school data, showing you where you stand, tracking your application progress, and managing your account and subscription. We do not use your academic data to train third-party AI models, and we do not sell your personal information to anyone.
          </p>
        </Section>

        <Section title="Local storage">
          <p style={{ margin: 0 }}>
            To make the app feel fast, some of your stats and filter preferences are cached in your browser's local storage, scoped to your account. Clearing your browser data will remove this cache without affecting the copy saved to your account.
          </p>
        </Section>

        <Section title="Who we share information with">
          <p style={{ marginBottom: 12 }}>We share information only with the service providers that make PreVisr work:</p>
          <ul style={{ margin: '0 0 12px', paddingLeft: 20 }}>
            <li style={{ marginBottom: 6 }}><strong style={{ color: TEXT }}>Supabase</strong> &mdash; authentication and database hosting.</li>
            <li style={{ marginBottom: 6 }}><strong style={{ color: TEXT }}>Stripe</strong> &mdash; payment processing for paid subscriptions.</li>
            <li><strong style={{ color: TEXT }}>Vercel</strong> &mdash; application hosting.</li>
          </ul>
          <p style={{ margin: 0 }}>We do not sell, rent, or trade your personal information to advertisers or data brokers.</p>
        </Section>

        <Section title="Your rights and choices">
          <p style={{ marginBottom: 12 }}>
            You can review or update your academic stats at any time from your dashboard. You can request a copy of your data or ask us to delete your account and associated data by contacting us at the email below &mdash; we'll complete deletion requests within 30 days.
          </p>
          <p style={{ margin: 0 }}>
            If you are a California resident, you have rights under the CCPA to know what personal information we hold and to request its deletion. If you are in the EU/EEA, you have equivalent rights under the GDPR, including the right to access, correct, or erase your data and to lodge a complaint with your local data protection authority.
          </p>
        </Section>

        <Section title="Data retention">
          <p style={{ margin: 0 }}>
            We retain your account information for as long as your account is active. If you delete your account, we remove your personal data from our active systems within 30 days, except where we're required to retain records for legal or accounting purposes (for example, payment records).
          </p>
        </Section>

        <Section title="Children's privacy">
          <p style={{ margin: 0 }}>
            PreVisr is intended for pre-medical students preparing to apply to medical school and is not directed at children under 13. We do not knowingly collect information from anyone under 13.
          </p>
        </Section>

        <Section title="Changes to this policy">
          <p style={{ margin: 0 }}>
            If we make material changes to this policy, we'll update the date above and, where appropriate, notify you by email.
          </p>
        </Section>

        <Section title="Contact us">
          <p style={{ margin: 0 }}>
            Questions about this policy or your data? Email us at <a href="mailto:support@previsr.com" style={{ color: BLUE }}>support@previsr.com</a>.
          </p>
        </Section>

        <div style={{ marginTop: 48, padding: '16px 20px', background: OFF_WHITE, border: '1px solid ' + BORDER, borderRadius: 10, fontSize: 13, color: TEXT_MUTED, lineHeight: 1.6 }}>
          Also see our <Link to="/terms" style={{ color: BLUE, fontWeight: 600 }}>Terms of Service</Link>.
        </div>
      </div>
    </div>
  );
}
